import axios from 'axios';

// Redux
import { forceLogoutUser } from '../redux/actions/authActions';

// Library
import { ActivityEvent, AlertType, AlertUpdate, BadgeType, Building, Floor, Hub, Incident, Room, StaffMember, StaffType, Badge, RedAlertTextGroup, NetworkNode } from '../lib';

// Constants
import { isNotNull, isString } from '../constants';
import User from '../lib/User';

// The API lives at api.<dashboard host>, e.g. krcs.simulalert.com calls
// api.krcs.simulalert.com, so one build serves every school. Local development
// uses the server on port 8000. REACT_APP_SERVER_URL overrides both at build
// time (DCS's build sets it, because its API is api-dcs.simulalert.com).
//
// The deploy workflows do not set REACT_APP_SERVER_URL, so hard-coding
// localhost here ships a dashboard that calls the user's own machine.
const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];

function defaultServerUrl() {
  const { protocol, hostname } = window.location;

  if (LOCAL_HOSTNAMES.includes(hostname)) {
    return 'http://localhost:8000/api';
  }

  return `${protocol}//api.${hostname.replace(/^www\./, '')}/api`;
}

// eslint-disable-next-line no-undef
const SERVER_URL = process.env.REACT_APP_SERVER_URL || defaultServerUrl();

const axiosWithCredentials = axios.create({
  withCredentials: true,
  baseURL: SERVER_URL
});

axiosWithCredentials.interceptors.response.use(response => response,
  error => {
    if (error && error.response?.status === 401) forceLogoutUser();

    // Try to get the core error message
    let rejectError = error;
    if (isString(error)) rejectError = error;
    else if (isString(error?.response)) rejectError = error.response;
    else if (isString(error?.response?.data)) rejectError = error.response.data;
    else if (isString(error?.response?.data?.message)) rejectError = error.response.data.message;
    return Promise.reject(rejectError ? rejectError : error);
  });

export function loginUser(email, password) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/login', { email: email, password: password }).then(res => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}
export function forgotPassword(email) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/forgot-password', { email: email }).then(res => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

export function resetPassword(token, password) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/reset-password', { token: token, password: password }).then(res => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

export function verifyMultiFactor(token) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/multi-factor/verify', { token: token }).then(res => {
      resolve(res);
    }).catch(error => {
      reject(error);
    });
  });
}

export function confirmMultiFactor(token) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/multi-factor/confirm', { token: token }).then(res => {
      resolve(res);
    }).catch(error => {
      reject(error);
    });
  });
}

export function setupMultiFactor() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/user/multi-factor/setup').then(res => {
      resolve(res && res.data && res.data.image ? res.data.image : res);
    }).catch(error => {
      reject(error);
    });
  });
}

export function logoutUser() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/logout').then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function updatePassword(newPassword) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/update', { password: newPassword }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function toggleUserActivity(userID) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/toggle/' + userID).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function createUser(user) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/', user).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function getStaffMember(staffMemberID) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/staff/' + staffMemberID).then(res => {
      resolve(StaffMember.thaw(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getRoom(roomID) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/rooms/' + roomID).then(res => {
      resolve(Room.thaw(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function deleteRoom(roomID) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.delete('/rooms/' + roomID).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function getHub(hubSerialNumber) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/hubs/' + hubSerialNumber).then(res => {
      resolve(Hub.thaw(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getBadge(badgeSerialNumber) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/badges/' + badgeSerialNumber).then(res => {
      resolve(Badge.thaw(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listBadgeTypes() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/badges/types').then(res => {
      resolve(BadgeType.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getBuilding(buildingID) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/buildings/' + buildingID).then(res => {
      resolve(Building.thaw(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getFloor(floorID) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/floors/' + floorID).then(res => {
      resolve(Floor.thaw(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listHubs() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/hubs').then(res => {
      resolve(Hub.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

// Access log

/**
 * Sessions started between from and to (Dates), plus any still active, and
 * the counts the Access log leads with. Super only.
 * @return {Promise<{ stats: object, sessions: object[], retentionDays: number }>}
 */
export function listAccessSessions({ from, to }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/access/sessions', { params: { from: from.toISOString(), to: to.toISOString() } }).then(res => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

/** One session with its whole trail, newest first. Super only. */
export function getAccessSession(sessionID) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/access/sessions/' + sessionID).then(res => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 * Tell the server which page the signed-in user is on, for their Access log
 * trail. Fire and forget: a failure here must never reach the user.
 */
export function reportActivity(path) {
  axiosWithCredentials.post('/session/activity', { path }).catch(() => {});
}

/**
 * Tell the server the signed-in user is still here, which gives the session
 * another full timeout from now. Resolves to the timeout in seconds, or null
 * if the request failed; never rejects.
 */
export function keepSessionAlive() {
  return axiosWithCredentials.post('/session/keepalive')
    .then(res => res.data?.seconds ?? null)
    .catch(() => null);
}

// Low badge power

/**
 * A badge's low power state and every notification sent about it:
 * { level, threshold, isLow, emailEnabled, textEnabled, log: [{ timestamp,
 * channel: 'email'|'text', automatic, to, error, level }] }, newest first.
 */
export function getBadgeLowPower(serialNumber) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/badges/' + serialNumber + '/low-power').then(res => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

/** Email the badge holder again. Admin, IT and Super. Resolves to the new state. */
export function sendBadgeLowPowerEmail(serialNumber) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/badges/' + serialNumber + '/low-power/email').then(res => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 * Show the charge reminder on the badge screen. Refused while an alert is in
 * force. Admin, IT and Super. Resolves to the new state.
 */
export function sendBadgeLowPowerText(serialNumber) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/badges/' + serialNumber + '/low-power/text').then(res => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

// Event log and alert history

/** The event log, newest first. `before` pages back from an ISO timestamp. */
export function listEvents({ limit = 20, before = null } = {}) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/events', { params: { limit, before: before ?? undefined } }).then(res => {
      resolve(ActivityEvent.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 * Alerts, newest first.
 * @param {{ mode?: 'all'|'real'|'drills', status?: string, limit?: number, offset?: number }} query
 * @return {Promise<{ total: number, alerts: Incident[] }>}
 */
export function listAlerts({ mode = 'all', status = null, limit = 50, offset = 0 } = {}) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/alerts', { params: { mode, status: status ?? undefined, limit, offset } }).then(res => {
      resolve({ total: res.data.total, alerts: Incident.thawList(res.data.alerts) });
    }).catch(error => {
      reject(error);
    });
  });
}

/** One alert with its timeline and dispatch record. */
export function getAlert(alertID) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/alerts/' + alertID).then(res => {
      resolve(Incident.thaw(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

/** Download the alert history as CSV, for the same filter as the list. */
export function downloadAlertsCsv({ mode = 'all', status = null } = {}) {
  return downloadCsv('/alerts/export.csv', { mode, status: status ?? undefined }, 'alert-history-' + mode + '.csv');
}

/**
 * Fetch a CSV with credentials and hand it to the browser as a file. The API
 * is on another origin, so a plain link would not carry the session cookie.
 */
function downloadCsv(path, params, fallbackName) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get(path, { params, responseType: 'blob' }).then(res => {
      const disposition = res.headers['content-disposition'] ?? '';
      const match = disposition.match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = match ? match[1] : fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

// Network (Super users only)

/** @return {Promise<{ networkAddress: number, nodes: NetworkNode[] }>} */
export function getNetwork() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/network').then(res => {
      resolve({ networkAddress: res.data.networkAddress, nodes: NetworkNode.thawList(res.data.nodes) });
    }).catch(error => {
      reject(error);
    });
  });
}


export function renameNetworkNode(nodeAddress, name) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch('/network/nodes/' + nodeAddress, { name }).then(res => {
      resolve(NetworkNode.thaw(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 * Download the node export. `latest` is one row per node; `history` is every
 * report in the last day. Fetched with credentials and handed to the browser
 * as a file, because the API lives on another origin and a plain link would
 * not carry the session cookie there.
 */
export function downloadNetworkCsv(scope = 'latest') {
  return downloadCsv('/network/export.csv', { scope }, 'network-' + scope + '.csv');
}

export function listBadges() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/badges').then(res => {
      resolve(Badge.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listUnassignedHubs() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/hubs/unassigned').then(res => {
      resolve(Hub.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listUnassignedBadges() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/badges/unassigned').then(res => {
      resolve(Badge.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listRooms() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/rooms').then(res => {
      resolve(Room.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listBuildings() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/buildings').then(res => {
      resolve(Building.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listFloors(buildingID = null) {
  return new Promise(function (resolve, reject) {
    const params = buildingID != null ? { buildingId: buildingID } : {};
    axiosWithCredentials.get('/floors', { params: params }).then(res => {
      resolve(Floor.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listStaff() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/staff').then(res => {
      resolve(StaffMember.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listUnassignedStaff() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/staff/unassigned').then(res => {
      resolve(StaffMember.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function listUsers() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/user/list').then(res => {
      resolve(User.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getUser(userID = null) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/user/', { params: userID != null ? { userId: userID } : null }).then(res => {
      resolve(User.thaw(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function getSchoolStatus() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/school/status').then(res => {
      // Retrieve the alert status
      if (res.data?.alertType == null) reject('Error: Unable to retrieve school status.');
      const alertStatus = AlertType.thaw(res.data.alertType);

      // Retrieve alert update, if it exists
      const alertUpdate = AlertUpdate.thaw(res.data?.alertUpdate);

      resolve({ status: alertStatus, roomId: alertUpdate?.getRoomID(), isDrill: alertUpdate?.getIsDrill() === true, lowBatteryPercent: res.data.lowBatteryPercent });
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 * Retrieves the most recent attempt to dispatch emergency services, so staff
 * can see the call progress and tell whether it actually reached anyone.
 * @returns {Promise<{ sid: string | null, status: string, toNumber: string, source: string, durationSeconds: number | null, errorCode: number | null, errorMessage: string | null, createdAt: string } | null>}
 */
export function getEmergencyCallStatus() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/school/emergency-call/status').then(res => {
      resolve(res.data?.call ?? null);
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 * Statuses that mean the dispatch did not reach anyone. Anything here should be
 * surfaced to staff as needing a redial or a manual call, not treated as a
 * successful dispatch.
 */
export const FAILED_EMERGENCY_CALL_STATUSES = ['rejected', 'failed', 'busy', 'no-answer', 'canceled'];

/**
 * Places another emergency call. Same endpoint the dashboard uses to dispatch
 * in the first place, so a redial is just another attempt and gets its own
 * record and status tracking.
 * @returns {Promise<object>}
 */
export function redialEmergencyCall() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/school/emergency-call').then(res => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 *
 * @param {number} [maximumCount] The maximum number of alerts to retrieve.
 * @returns {Promise<(AlertUpdate | null)[]>}
 */
export function getAlertUpdates(maximumCount = undefined) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/school/alert-updates', { params: { maximumCount } }).then(res => {
      resolve(AlertUpdate.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

/**
 * @param {AlertType} alertStatus
 * @param {boolean} isDrill run it as a drill: every device shows it, but no red
 *   alert texts are sent and no emergency call is placed. The server refuses a
 *   drill while a real alert is in force.
 */
export function setSchoolStatus(alertStatus, isDrill = false) {
  return new Promise(function (resolve, reject) {
    // isDrill is sent only for a drill. A server without drill mode rejects
    // any request carrying the field, so sending `false` would break every
    // real alert against it; this way a real alert works on any server, and
    // a drill is refused rather than run as a real alert.
    const body = { alertStatus: alertStatus.getDashboardStatus() };
    if (isDrill === true) body.isDrill = true;
    axiosWithCredentials.post('/school/status', body).then(res => {
      resolve(res);
    }).catch(error => {
      reject(error);
    });
  });
}

export function setRoomStatus(roomID, alertStatus) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/rooms/status', { roomId: roomID, alertStatus: alertStatus.getDashboardStatus() }).then(res => {
      resolve(res);
    }).catch(error => {
      reject(error);
    });
  });
}

export function changeUserPassword(userID, password) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/password', {
      userId: userID,
      password: password
    }
    ).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateUser(user) {

  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/update', {
      userId: user.userID,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function toggleMobileAccess(userID, hasAccess) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/mobile', {
      userId: userID,
      hasAccess: hasAccess
    }
    ).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function getAdminSettings() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/admin/settings').then(res => {
      if (!isNotNull(res.data)) reject('Something went wrong.');
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

export function resetUserPassword(userID) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/reset/' + userID).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function patchAdminSettings(settings) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch('/admin/settings', settings).then((res) => {
      resolve(res);
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateStaffMember(staffMemberObj) {
  return new Promise(function(resolve, reject) {
    var data = new FormData();
    if (staffMemberObj.staffMemberImage) data.append('image', staffMemberObj.staffMemberImage);
    data.append('staffId', staffMemberObj.id);
    data.append('firstName', staffMemberObj.firstName);
    data.append('lastName', staffMemberObj.lastName);
    if (staffMemberObj.phoneNumber) data.append('phoneNumber', staffMemberObj.phoneNumber);
    if (staffMemberObj.email) data.append('email', staffMemberObj.email);
    data.append('staffTypeId', staffMemberObj.staffTypeID);
    if (staffMemberObj.badgeSerialNumber) data.append('badgeSerialNumber', staffMemberObj.badgeSerialNumber);
    if (staffMemberObj.assignedRoomID) data.append('assignedRoomId', staffMemberObj.assignedRoomID);

    axiosWithCredentials.patch(
      '/staff',
      data,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    ).then((res) => {
      resolve(res);
    })
      .catch((error) => {
        reject(error);
      });
  });
}

export function resetUser2fa(userID) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/user/multi-factor/reset/', {
      userId: userID
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function listStaffTypes() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/staff/types/').then(res => {
      resolve(StaffType.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function createBuilding({ name, code }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/buildings', {
      name,
      code
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateBuilding({ id, name }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.patch(`/buildings/${id}`, {
      name
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function deleteBuilding(id) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.delete(`/buildings/${id}`).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function getSetupMode() {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.get('/admin/setup').then((res) => {
      resolve(res.data);
    }).catch(error => {
      reject(error);
    });
  });
}

export function toggleSetupMode(setupMode) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/admin/setup', { setupMode: setupMode }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function setSetupMode(value) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/admin/setup', {
      setupMode: value
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function createHub({ serialNumber, hubType, floorID, coordinate, roomID, notes }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/hubs', {
      serialNumber,
      hubType,
      roomId: roomID,
      notes,
      coordinate,
      floorId: floorID
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function deleteHub(serialNumber) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.delete(`/hubs/${serialNumber}`).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function createBadge({ serialNumber, staffMemberID, badgeTypeID }) {
  return new Promise(function (resolve, reject) {
    axiosWithCredentials.post('/badges', {
      serialNumber,
      staffMemberId: staffMemberID,
      badgeTypeId: badgeTypeID
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function createRoom(floorID, identifier, vertices) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/rooms', {
      floorId: floorID,
      identifier: identifier,
      vertices
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateRoom(roomObj) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.patch('/rooms', {
      roomId: roomObj.id,
      floorId: roomObj.floorID,
      identifier: roomObj.identifier,
      vertices: roomObj.vertices
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function createFloor({ buildingID, zIndex, identifier, floorPlanImage = null, latLeftTop, longLeftTop, imageWidthM }) {
  return new Promise(function(resolve, reject) {
    var data = new FormData();
    if (floorPlanImage != null) data.append('image', floorPlanImage);
    data.append('buildingId', buildingID);
    data.append('zIndex', zIndex);
    data.append('identifier', identifier);
    data.append('latLeftTop', latLeftTop);
    data.append('longLeftTop', longLeftTop);
    data.append('imageWidthM', imageWidthM);

    axiosWithCredentials.post(
      '/floors/',
      data,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    ).then((res) => {
      resolve(res);
    })
      .catch((error) => {
        reject(error);
      });
  });
}

export function updateFloor({ id, buildingID, zIndex, identifier, floorPlanImage = null, latLeftTop, longLeftTop, imageWidthM }) {
  return new Promise(function(resolve, reject) {
    var data = new FormData();
    if (floorPlanImage != null) data.append('image', floorPlanImage);
    data.append('buildingId', buildingID);
    data.append('zIndex', zIndex);
    data.append('identifier', identifier);
    data.append('latLeftTop', latLeftTop);
    data.append('longLeftTop', longLeftTop);
    data.append('imageWidthM', imageWidthM);

    axiosWithCredentials.patch(
      `/floors/${id}`,
      data,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    ).then((res) => {
      resolve(res);
    })
      .catch((error) => {
        reject(error);
      });
  });
}

export function deleteFloor(id) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.delete(`/floors/${id}`).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function createStaffMember(staffMemberObj) {
  return new Promise(function(resolve, reject) {
    var data = new FormData();
    if (staffMemberObj.staffMemberImage) data.append('image', staffMemberObj.staffMemberImage);
    data.append('firstName', staffMemberObj.firstName);
    data.append('lastName', staffMemberObj.lastName);
    if (staffMemberObj.phoneNumber) data.append('phoneNumber', staffMemberObj.phoneNumber);
    if (staffMemberObj.email) data.append('email', staffMemberObj.email);
    data.append('staffTypeId', staffMemberObj.staffTypeID);
    if (staffMemberObj.badgeSerialNumber) data.append('badgeSerialNumber', staffMemberObj.badgeSerialNumber);
    if (staffMemberObj.assignedRoomID) data.append('assignedRoomId', staffMemberObj.assignedRoomID);

    axiosWithCredentials.post(
      '/staff/',
      data,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    ).then((res) => {
      resolve(res);
    })
      .catch((error) => {
        reject(error);
      });
  });
}

export function deleteBadge(serialNumber) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.delete(`/badges/${serialNumber}`).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

/** Deactivate or reactivate a staff member. The record is kept either way. */
export function setStaffMemberActive(staffMemberID, isActive) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/staff/toggle', {
      staffMemberId: staffMemberID,
      isActive: isActive === true
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function deleteStaffMember(staffMemberID) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post('/staff/toggle', {
      staffMemberId: staffMemberID,
      isActive: false
    }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function listRedAlertTextGroups() {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get('/red-alert-text-group').then(res => {
      resolve(RedAlertTextGroup.thawList(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function deleteRedAlertTextContact(id) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.delete('/red-alert-text-group/contact/' + id).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function getRedAlertTextGroup(id) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.get('/red-alert-text-group/' + id).then(res => {
      resolve(RedAlertTextGroup.thaw(res.data));
    }).catch(error => {
      reject(error);
    });
  });
}

export function addRedAlertTextContact(id, { name, phoneNumber }) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post(`/red-alert-text-group/${id}/contact`, { name, phoneNumber }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}

export function updateRedAlertTextGroup(id, { message }) {
  return new Promise(function(resolve, reject) {
    axiosWithCredentials.post(`/red-alert-text-group/${id}`, { message }).then(() => {
      resolve();
    }).catch(error => {
      reject(error);
    });
  });
}